import { IModelFileType, ModelFileType, ModelLoaderOptions } from "@centricsoftware/model-viewer";
import csi from "csi/main";
import { C8Node } from "csi/typings";
import { dataURLtoBlobURL, getFileNameExtension } from "csi/utility/fileUtility";
import { getAlternateFileRequestUrl, getFileVaultRequestUrl } from "src/c8-platform/core/request";
import { getModelFile, getModelFileUrl, TYPE_MATERIALDRAPE, isLoaderResourceURLValid } from "csi/model/document";

const DRACO_DECODER_PATH = "draco/gltf/";
const RHINO_PATH = "rhino3dm/";
const DEFAULT_MATERIAL_GEOMETRY_PATH = "assets/fabricDefault.glb";
export const ALVA_MESH = "AlvaMesh";
export const AVATAR = "Avatar";

export function getResourceURLModifier(revisionNode:C8Node): (url: string) => string {
	return ((url) => {
		const isRelativeURL = url.indexOf("/") === 0;

		// If the URL has a .tif extension, then grab the converted png file from the file vault which ends with -converted-TIF.png
		if (url.endsWith(".tif")) {
			url = url.replace(".tif", "-converted-TIF.png");
		}

		// If the URL is a data URL, transform it to a blob URL to avoid cross origin issues
		if (url.startsWith("data:")) {
			url = dataURLtoBlobURL(url);
		}

		if (url.startsWith("textures\\")) {
			// U3M texture folder is flattened on upload so remove the textures\\ from the url
			const path = getAlternateFileRequestUrl(revisionNode.$URL, url.replace("textures\\", ""), true);
			return path;
		}

		if (url.startsWith("textures/")) {
			// U3M texture folder is flattened on upload so remove the textures/ from the url
			const path = getAlternateFileRequestUrl(revisionNode.$URL, url.replace(/^.*[\\\/]/, ""), true);
			return path;
		}

		// If the URL is a rhino3dm library file, a relative URL, a URL that contains the file value, or if the node is a material drape then do not modify it
		// This includes assests URls that start with 'blob: ', modelFile URLs, and mtl file URLs. 
		// These files can be loaded directly from the file vault
		if (url.includes("rhino3dm") || !isRelativeURL || isLoaderResourceURLValid(url, revisionNode) || revisionNode.$Type === TYPE_MATERIALDRAPE) {
			return url;
		}

		// Otherwise, get the alternate file URL for the file
		const path = getAlternateFileRequestUrl(revisionNode.$URL, url.replace(/^.*[\\\/]/, ""), true);
        
		return path;
	});
}

export function getDracoPath(): string {
	return csi.urlForModule(DRACO_DECODER_PATH);
}

export function getRhinoPath(): string {
	return csi.urlForModule(RHINO_PATH);
}

export function getMaterialDefaultPath(): string {
	return csi.urlForModule(DEFAULT_MATERIAL_GEOMETRY_PATH);
}

/** Return a single ModelFile on a revision node */
export function getAlternateFileOnRevisionNode(revisionNode: C8Node): ModelFileType | null {
	const allFilesOnTheRevisionNode = getModelFileUrl(revisionNode);
	// TODO: Potentailly add support for multiple files on a single revision node (e.g u3ma)
	return allFilesOnTheRevisionNode[0];
}
	
/** Return the model URL on a given revision node */
export function getModelUrl(file: string | ArrayBuffer, revisionNode: C8Node): string | ArrayBuffer {
	if (file instanceof ArrayBuffer) {
		return file;
	}
	const modelFile = getModelFile(revisionNode, file);
	return getFileVaultRequestUrl(modelFile, false);
}

/** Return file type of the model on a given revision node */
export function getFileType(revisionNode: C8Node, file: string | ArrayBuffer, isEncrypted: boolean): IModelFileType {
	if (isEncrypted) {
		return "glb";
	}
	
	if (revisionNode.$Type === TYPE_MATERIALDRAPE) {
		return "obj";
	}

	const extension = getFileNameExtension(file as string) as IModelFileType;
	return extension;
}

/** Return ModelViewer Loader options for static resources */
export function getModelViewerLoaderOptions(): ModelLoaderOptions {
	const dracoDecoderPath = getDracoPath();
	const rhinoLibraryPath = getRhinoPath();
	const materialDefaultGeometryPath = getMaterialDefaultPath();
    
	const options: ModelLoaderOptions = {
		dracoDecoderPath,
		rhinoLibraryPath,
		materialDefaultGeometryPath,
	};
	return options;
}